import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Download, BookOpen } from 'lucide-react';
import axios from 'axios';
import ModernCard from '../components/ModernCard';
import { API_ENDPOINTS } from '../config/api';
import './NotesPage.css';

const NotesPage = () => {
  const { unitName } = useParams();
  const [originalUnitName, setOriginalUnitName] = useState('');
  const [notesFiles, setNotesFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // First get all units to find the original name
    const findOriginalUnitName = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.UNITS);
        const units = response.data;
        const originalName = units.find(unit => {
          const slug = unit.toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_-]+/g, '-')
            .replace(/^-+|-+$/g, '');
          return slug === unitName;
        });
        
        if (originalName) {
          setOriginalUnitName(originalName);
          fetchNotesFiles(originalName);
        } else {
          setError('Unit not found');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error finding unit name:', err);
        setError('Failed to load unit. Please try again.');
        setLoading(false);
      }
    };

    findOriginalUnitName();
  }, [unitName]);

  const fetchNotesFiles = async (unitName) => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.UNIT_FILES(unitName));
      const notes = response.data.files.filter(file => file.type === 'notes');
      setNotesFiles(notes);
      setError(null);
    } catch (err) {
      console.error('Error fetching notes:', err);
      setError('Failed to load notes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (file, event) => {
    event.preventDefault();
    const downloadUrl = `${process.env.REACT_APP_API_URL}${file.download_url}`;
    window.open(downloadUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="notes-page">
        <div className="page-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading notes...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notes-page">
        <div className="page-container">
          <div className="error-state">
            <h3>Error Loading Notes</h3>
            <p>{error}</p>
            <button className="retry-button" onClick={() => fetchNotesFiles(originalUnitName)}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="notes-page">
      <div className="page-container">
        {/* Header */}
        <header className="page-header">
          <Link to={`/unit/${unitName}`} className="back-button">
            <ArrowLeft size={20} />
            <span>Back to {originalUnitName}</span>
          </Link>
          <h1 className="page-title">Notes</h1>
          <p className="page-subtitle">{originalUnitName} - Lecture Notes and Study Materials</p>
        </header>

        {/* Notes Files */}
        {notesFiles.length > 0 ? (
          <div className="files-grid">
            {notesFiles.map((file) => (
              <ModernCard
                key={file.name}
                icon={FileText}
                title={file.name}
                description="Lecture notes and study materials"
                buttonText={
                  <>
                    <Download size={16} />
                    Download
                  </>
                }
                onClick={(event) => handleDownload(file, event)}
                variant="file"
                className="file-card"
              />
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <BookOpen size={48} />
            <h3>No Notes Found</h3>
            <p>No lecture notes are available for this unit.</p>
            <Link to={`/unit/${unitName}`} className="back-link">
              Back to Unit
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesPage;
